import { useCallback, useState } from 'react';
import { usePagedList } from '../../hooks/usePagedList';
import { ListCount, LoadMore } from './AdminPaging';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { IDEA_LIMITS, IDEA_STATUSES, type IdeaStatus } from '@twinden/shared';
import { adminApi, ApiError, type AdminIdea } from '../../lib/api';
import { appLocale } from '../../lib/format';

// Shape Twinden for the system root (M55, docs/run-by-families.md D198–D200):
// who posted each idea (shown nowhere else), its status and note, edits,
// hiding and merging. The admin panel is English-only, like its other tabs.

const STATUS_LABEL: Record<IdeaStatus, string> = {
  open: 'Open',
  planned: 'Planned',
  building: 'Building',
  done: 'Done',
  not_now: 'Not now',
};

function IdeaCard({
  idea,
  targets,
  onChange,
}: {
  idea: AdminIdea;
  targets: AdminIdea[];
  onChange: () => Promise<void>;
}) {
  const toast = useToast();
  const [status, setStatus] = useState<IdeaStatus>(idea.status);
  const [note, setNote] = useState(idea.statusNote ?? '');
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(idea.title);
  const [body, setBody] = useState(idea.body ?? '');
  const [mergeInto, setMergeInto] = useState('');
  const [busy, setBusy] = useState(false);

  const act = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await work();
      await onChange();
      toast({ title: done, status: 'success', duration: 2000 });
    } catch (err) {
      toast({
        title: 'Could not save',
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const merged = idea.mergedIntoId
    ? targets.find((target) => target.id === idea.mergedIntoId)
    : null;
  const statusChanged =
    status !== idea.status || note.trim() !== (idea.statusNote ?? '');
  // "Not now" says why (D199).
  const needsNote = status === 'not_now' && !note.trim();

  return (
    <Box
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={4}
      opacity={idea.hiddenAt ? 0.75 : 1}
    >
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="flex-start" flexWrap="wrap">
          <Box minW={0} flex="1">
            {editing ? (
              <VStack align="stretch" spacing={2}>
                <Input
                  value={title}
                  maxLength={IDEA_LIMITS.TITLE_MAX}
                  aria-label="Title"
                  onChange={(e) => setTitle(e.target.value)}
                />
                <Textarea
                  value={body}
                  maxLength={IDEA_LIMITS.BODY_MAX}
                  aria-label="Details"
                  onChange={(e) => setBody(e.target.value)}
                />
              </VStack>
            ) : (
              <>
                <Text fontWeight={800} fontSize="18px">
                  {idea.title}
                </Text>
                {idea.body && <Text whiteSpace="pre-wrap">{idea.body}</Text>}
              </>
            )}
          </Box>
          <HStack>
            {idea.hiddenAt && (
              <Badge colorScheme="red" borderRadius="full" px={2}>
                {merged ? 'Merged' : 'Hidden'}
              </Badge>
            )}
            <Badge
              colorScheme="gray"
              borderRadius="full"
              px={2}
              textTransform="none"
            >
              {idea.votesCount} {idea.votesCount === 1 ? 'vote' : 'votes'}
            </Badge>
          </HStack>
        </HStack>

        <Text fontSize="sm" color="muted">
          {idea.author
            ? `@${idea.author.handle}${idea.author.deleted ? ' (deleted account)' : ''} · in ${idea.author.familiesCount} ${idea.author.familiesCount === 1 ? 'family' : 'families'}`
            : 'Poster unknown'}
          {' · '}
          {new Date(idea.createdAt).toLocaleDateString(appLocale())}
          {' · votes from '}
          {idea.voterFamiliesCount}{' '}
          {idea.voterFamiliesCount === 1 ? 'family' : 'families'}
          {idea.mergedCount > 0 && ` · merged from ${idea.mergedCount}`}
        </Text>
        {merged && (
          <Text fontSize="sm" color="muted">
            Merged into “{merged.title}”
          </Text>
        )}

        {!idea.mergedIntoId && (
          <>
            <HStack spacing={2} flexWrap="wrap" align="flex-start">
              <Select
                value={status}
                maxW="180px"
                aria-label="Status"
                onChange={(e) => setStatus(e.target.value as IdeaStatus)}
              >
                {IDEA_STATUSES.map((key) => (
                  <option key={key} value={key}>
                    {STATUS_LABEL[key]}
                  </option>
                ))}
              </Select>
              <Textarea
                flex="1"
                minW="220px"
                size="sm"
                rows={2}
                value={note}
                maxLength={IDEA_LIMITS.NOTE_MAX}
                placeholder={
                  status === 'not_now'
                    ? 'Why not now (required, shown publicly)'
                    : 'A note under the idea (optional, shown publicly)'
                }
                onChange={(e) => setNote(e.target.value)}
              />
            </HStack>
            {needsNote && (
              <Text fontSize="sm" color="danger">
                Say why it’s not for now.
              </Text>
            )}
            <HStack spacing={2} flexWrap="wrap">
              {statusChanged && (
                <Button
                  size="sm"
                  isDisabled={busy || needsNote}
                  onClick={() =>
                    void act(
                      () =>
                        adminApi.updateIdea(idea.id, {
                          status,
                          note: note.trim() || null,
                        }),
                      'Status saved'
                    )
                  }
                >
                  Save status
                </Button>
              )}
              {editing ? (
                <>
                  <Button
                    size="sm"
                    isDisabled={busy || !title.trim()}
                    onClick={() =>
                      void act(async () => {
                        await adminApi.updateIdea(idea.id, {
                          title: title.trim(),
                          body: body.trim() || null,
                        });
                        setEditing(false);
                      }, 'Idea saved')
                    }
                  >
                    Save text
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="gray"
                    onClick={() => {
                      setTitle(idea.title);
                      setBody(idea.body ?? '');
                      setEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => setEditing(true)}
                >
                  Edit text
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                colorScheme="gray"
                color={idea.hiddenAt ? undefined : 'danger'}
                isDisabled={busy}
                onClick={() =>
                  void act(
                    () =>
                      adminApi.updateIdea(idea.id, { hidden: !idea.hiddenAt }),
                    idea.hiddenAt ? 'Shown again' : 'Hidden'
                  )
                }
              >
                {idea.hiddenAt ? 'Unhide' : 'Hide'}
              </Button>
            </HStack>
            {targets.length > 0 && (
              <HStack spacing={2} flexWrap="wrap">
                <Select
                  size="sm"
                  maxW="320px"
                  value={mergeInto}
                  aria-label="Merge into"
                  onChange={(e) => setMergeInto(e.target.value)}
                >
                  <option value="">Merge into…</option>
                  {targets
                    .filter((target) => target.id !== idea.id)
                    .map((target) => (
                      <option key={target.id} value={target.id}>
                        {target.title}
                      </option>
                    ))}
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  isDisabled={busy || !mergeInto}
                  onClick={() =>
                    void act(
                      () => adminApi.mergeIdea(idea.id, mergeInto),
                      'Merged: its votes moved'
                    )
                  }
                >
                  Merge
                </Button>
              </HStack>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
}

export function IdeasTab() {
  // 50 a page, newest first (#73).
  const fetchPage = useCallback(
    async (cursor: string | null) => (await adminApi.getIdeas({ cursor })).data,
    []
  );
  const list = usePagedList<AdminIdea, Awaited<ReturnType<typeof fetchPage>>>(
    fetchPage
  );
  const ideas = list.items;

  const loadingState = useLoadingState({
    loading: !ideas && !list.error,
    hasData: ideas !== null,
    error: list.error,
    retry: list.retry,
  });
  if (!ideas || loadingState.show) return <LoadingPanel state={loadingState} />;
  // Only visible ideas can take a merge: those loaded so far (#73).
  const targets = ideas.filter((idea) => !idea.hiddenAt && !idea.mergedIntoId);
  return (
    <VStack align="stretch" spacing={4} maxW="760px">
      <Text color="muted">
        Ideas from Shape Twinden (twinden.com/ideas), newest first. Families see
        them only as “from a Twinden family”; the poster is shown here alone.
        Set a status and an optional public note (needed for Not now), edit any
        title or details, hide an idea, or merge it into another: its votes move
        there without counting anyone twice.
      </Text>
      <ListCount list={list} one="idea" many="ideas" />
      {ideas.length === 0 ? (
        <Text color="muted">No ideas yet.</Text>
      ) : (
        ideas.map((idea) => (
          <IdeaCard
            key={`${idea.id}:${idea.updatedAt}`}
            idea={idea}
            targets={targets}
            onChange={list.reload}
          />
        ))
      )}
      <LoadMore list={list} />
    </VStack>
  );
}
